import type { AnchorHTMLAttributes, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'brand' | 'outline' | 'ghost' | 'light'

export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-[color,background-color,border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60 [&_svg]:size-4 [&_svg]:shrink-0'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
    brand: 'bg-brand text-brand-foreground shadow-sm hover:bg-brand/85',
    outline: 'border border-foreground/20 bg-transparent text-foreground hover:border-foreground/40 hover:bg-foreground/5',
    ghost: 'text-foreground hover:bg-accent',
    light: 'border border-white/30 bg-white/10 text-white hover:bg-white/20'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-base'
}

export const buttonStyles = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string =>
    cn(BASE, VARIANTS[variant], SIZES[size], className)

interface ButtonLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link href={href} className={buttonStyles(variant, size, className)} {...props}>
            {children}
        </Link>
    )
}
